import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Shield, AlertTriangle } from "lucide-react";

interface AdminGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export function AdminGuard({ children, fallback }: AdminGuardProps) {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    if (!isLoading) {
      if (!user) {
        // Not authenticated, redirect to login
        console.log('🚫 AdminGuard: No user found, redirecting to login');
        navigate('/login');
        return;
      }

      console.log('🔍 AdminGuard: Checking admin access for:', user.email);

      if (user.email !== 'admin@bizamply.com') {
        // Not admin, redirect to dashboard
        console.log('🚫 AdminGuard: Non-admin user trying to access admin area:', user.email);
        navigate('/dashboard');
        return;
      }

      // Admin user, allow access
      console.log('✅ AdminGuard: Admin access granted for:', user.email);
      setIsChecking(false);
    }
  }, [user, isLoading, navigate]);

  // Show loading while checking authentication
  if (isLoading || isChecking) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100 mx-auto mb-4"></div>
          <p className="text-gray-500 dark:text-gray-400">Verifying admin access...</p>
        </div>
      </div>
    );
  }

  // Show fallback if provided, otherwise show access denied
  if (!user || user.email !== 'admin@bizamply.com') {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="min-h-screen bg-white dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="mb-6">
            <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Access Denied
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              You don't have permission to access this area. This section is restricted to administrators only.
            </p>
          </div>

          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
              <Shield className="h-5 w-5" />
              <span className="font-medium">Admin Access Required</span>
            </div>
            <p className="text-red-700 dark:text-red-300 text-sm mt-1">
              Only users with admin@bizamply.com email can access customer management.
            </p>
          </div>

          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Admin user, render children
  return <>{children}</>;
}

// Hook for checking admin status
export function useAdminCheck() {
  const { user, isLoading } = useAuth();

  const isAdmin = user?.email === 'admin@bizamply.com';
  const isChecking = isLoading;

  return {
    isAdmin,
    isChecking,
    user
  };
}
